import type {
	BoardFragmentFragment,
	TeamFragmentFragment,
} from '@apps/www/src/__generated__/graphql';
import SVBoardOptionsContainer from '@apps/www/src/www/containers/SVBoardOptionsContainer';
import usePersistentSetting, {
	PersistentSettingKeys,
} from '@apps/www/src/www/hooks/usePersistentSetting';
import useTeamHomePath from '@apps/www/src/www/hooks/useTeamHomePath';
import { closestCenter, DndContext, MeasuringStrategy } from '@dnd-kit/core';

import { rectSortingStrategy, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import SVLink from '@pkgs/shared-client/components/SVLink';
import SVTopNavSpacer from '@pkgs/shared-client/components/SVTopNavSpacer';
import cn from '@pkgs/shared-client/helpers/cn';
import matchLocation from '@pkgs/shared-client/helpers/matchLocation';
import plural from '@pkgs/shared-client/helpers/plural';
import { useBoardReorder } from '@pkgs/shared-client/hooks/useBoardReorder';
import useEventCallback from '@pkgs/shared-client/hooks/useEventCallback';
import IconMoreSVG from '@pkgs/shared-client/img/icon-more-inlined.svg';
import IconPlus2SVG from '@pkgs/shared-client/img/icon-plus2-inlined.svg';
import BoardUserRole from '@pkgs/shared/enums/BoardUserRole';
import SidebarOpenState from '@pkgs/shared/enums/SidebarOpenState';
import canReorderBoard from '@pkgs/shared/helpers/canReorderBoard';
import { useRouter } from 'next/router';
import React, { useMemo, useRef, useState } from 'react';

const SORTABLE_BOARD_ITEM_CLASS = 'sortable-board-item';

const DRAGGING_Z_INDEX = 10;
const DROPDOWN_OPEN_Z_INDEX = 8;

const TYPES = {
	USER: 'user',
	TEAM: 'team',
} as const;

/**
 * Gets the appropriate board link based on type and current path
 */
const getBoardLink = (
	type: ValueOf<typeof TYPES>,
	pathname: string,
	board: BoardFragmentFragment,
	teamHomePath: string,
	team?: TeamFragmentFragment,
): string => {
	if (type === TYPES.USER) {
		return board.url;
	}

	// If the board is the default team feed, redirect to the team feed page
	if (team && board._id === team.feedBoardID) {
		return teamHomePath;
	}

	// For team boards, remove "/boards" from URL when not already on a boards page
	if (!pathname.includes('/boards')) {
		return board.url.replace('/boards', '');
	}

	return board.url;
};

type Props = {
	type: ValueOf<typeof TYPES>;
	openState: ValueOf<typeof SidebarOpenState>;
	boards: BoardFragmentFragment[];
	isOwner: boolean;
	onNewBoardClick: () => void;
	onToggleSidebar?: () => void;
	children: React.ReactNode;
	lastThumbnails?: string[];
	team?: TeamFragmentFragment;
};

const _BoardItem = React.memo(
	({
		type,
		openState,
		board,
		team,
		teamHomePath,
		isDragging,
		boardRole,
		dragListeners,
	}: Pick<Props, 'type' | 'openState'> & {
		board: BoardFragmentFragment;
		team?: TeamFragmentFragment;
		teamHomePath: string;
		isDragging?: boolean;
		boardRole: ValueOf<typeof BoardUserRole>;
		dragListeners?: any;
	}) => {
		const router = useRouter();
		const linkTo = getBoardLink(type, router.pathname, board, teamHomePath, team);
		const isActive = matchLocation(router.asPath, linkTo);
		const [isHovered, setIsHovered] = useState(false);
		const [isDropdownOpen, _setIsDropdownOpen] = useState(false);
		const wrapperRef = useRef<HTMLLIElement>(null);

		const setIsDropdownOpen = useEventCallback((isOpen: boolean) => {
			// When opening or closing, we need to give a z-index to the wrapper for the dropdown to be on top of the other sortable items
			const closestSortableItem = wrapperRef.current?.closest(
				`.${SORTABLE_BOARD_ITEM_CLASS}`,
			) as HTMLElement;

			if (closestSortableItem) {
				if (isOpen) {
					closestSortableItem.style.zIndex = DROPDOWN_OPEN_Z_INDEX.toString();
				} else {
					closestSortableItem.style.removeProperty('z-index');
				}
			}

			_setIsDropdownOpen(isOpen);
		});

		return (
			<li
				className="-lg:border-gray-800 -lg:border -lg:rounded-[10px] -lg:min-w-[200px] block min-h-full items-center justify-center"
				ref={wrapperRef}
			>
				<div
					className="group relative flex h-full min-h-full w-full"
					onMouseEnter={() => setIsHovered(true)}
					onMouseLeave={() => setIsHovered(false)}
				>
					<span
						className={cn(
							'absolute inset-0 block rounded-[10px] bg-gray-900 opacity-0 transition-opacity duration-200',
							(isActive || isHovered || isDropdownOpen || isDragging) &&
								'opacity-100',
							isActive && 'bg-gray-950',
						)}
					/>

					{isActive && (
						<span
							className={cn(
								'absolute inset-0 block rounded-[10px] border-[1.5px] transition-colors',
								openState === SidebarOpenState.MINIMAL
									? 'border-gray-400'
									: 'border-gray-950',
							)}
						/>
					)}

					<div
						className={cn(
							'absolute inset-0',
							openState === SidebarOpenState.MINIMAL ? 'w-full' : 'w-[85%]',
							isDragging ? 'cursor-grabbing' : 'cursor-pointer',
						)}
						onClick={() => {
							if (!isDragging) {
								router.push(linkTo);
							}
						}}
						{...dragListeners}
					/>
					<SVLink
						to={linkTo}
						className="pointer-events-none relative flex h-full min-h-full w-full items-center"
						tabIndex={isDragging ? -1 : undefined}
					>
						<div className="flex h-full min-h-full w-full items-center gap-3 px-[6px] py-[4px] pr-8">
							<div
								className="block h-10 w-10 flex-shrink-0 rounded bg-gray-800 bg-cover"
								style={
									board.thumbnails.length > 0
										? {
												backgroundImage: `url(${board.thumbnails[0].image.thumbnail})`,
										  }
										: {}
								}
							/>

							<div className="flex min-w-0 flex-grow flex-col">
								<span
									className={cn(
										'type-small truncate',
										isActive && 'text-primary',
									)}
								>
									{board.name}
								</span>
								<span className="type-small truncate text-gray-500">
									{plural(board.itemsCount, 'Save')}
								</span>
							</div>
						</div>
					</SVLink>

					{openState === SidebarOpenState.OPEN && (
						<div className="-lg:hidden absolute right-2.5 top-1/2 z-20 -translate-y-1/2">
							<SVBoardOptionsContainer
								type={board.ownershipType}
								board={board}
								disableHover
								role={boardRole}
								triggerIcon={IconMoreSVG}
								iconClassName={cn(
									'text-gray-600 transition-colors duration-200',
									isHovered || isDropdownOpen ? 'opacity-100' : 'opacity-0',
									'hover:text-gray-200',
								)}
								onGrid="w-9 h-9 min-h-9 min-w-9 bg-transparent"
								onOpenChange={setIsDropdownOpen}
							/>
						</div>
					)}
				</div>
			</li>
		);
	},
);

const NewBoardItem = React.memo(
	({
		onClick,
		type,
		openState,
	}: {
		onClick: () => void;
		type: ValueOf<typeof TYPES>;
		openState: ValueOf<typeof SidebarOpenState>;
	}) => {
		return (
			<li className="-lg:border-gray-800 -lg:border -lg:rounded-[10px] -lg:min-w-[200px] block min-h-full items-center justify-center">
				<button
					onClick={onClick}
					className="group relative block h-full w-full items-center justify-center"
				>
					<span className="absolute block h-full w-full rounded-[10px] bg-gray-900 opacity-0 group-hover:opacity-100" />
					<span className="relative flex h-full items-center gap-3 p-[6px] ">
						<span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded bg-gray-950">
							<IconPlus2SVG className="h-3 w-3 text-gray-400" />
						</span>
						{openState === SidebarOpenState.OPEN && (
							<span className="flex min-h-full min-w-0 flex-grow flex-col items-start justify-center">
								<span className="type-small truncate">
									New {type === TYPES.TEAM ? 'team' : ''} board
								</span>
							</span>
						)}
					</span>
				</button>
			</li>
		);
	},
);

const _ToggleButton = ({
	openState,
	onToggle,
}: Pick<Props, 'openState'> & {
	onToggle?: () => void;
}) => {
	const label =
		openState === SidebarOpenState.OPEN
			? 'Minimize side menu'
			: openState === SidebarOpenState.MINIMAL
			? 'Hide side menu'
			: 'Show side menu';

	return (
		<div className="-lg:hidden text-secondary z-30 mb-2 inline-flex h-12 w-[40px] items-center justify-center pt-[6px]">
			<button
				onClick={onToggle}
				aria-label={label}
				title={label}
				className="relative flex h-10 w-10 items-center justify-center rounded-full text-gray-400 transition-colors duration-200 hover:bg-gray-50 hover:bg-opacity-10"
			>
				<div className="relative flex h-5 w-5 flex-col items-center justify-center">
					<span
						className={cn(
							'absolute block h-[1.5px] w-4 rounded-full bg-current transition-transform duration-300',
							// isVisible ? 'rotate-45 transform' : 'translate-y-[-4px]',
							'translate-y-[-4px]',
						)}
					/>
					<span
						className={cn(
							'absolute block h-[1.5px] w-4 rounded-full bg-current transition-transform duration-300',
							// isVisible ? '-rotate-45 transform' : 'translate-y-[4px]',
							'translate-y-[4px]',
						)}
					/>
				</div>
			</button>
		</div>
	);
};

const _AllSavedItems = ({
	thumbnails,
	openState,
	teamHomePath,
}: Pick<Props, 'openState'> & {
	thumbnails: string[];
	teamHomePath: string;
}) => {
	const router = useRouter();
	const linkTo = teamHomePath;
	const isActive = matchLocation(router.asPath, linkTo);

	return (
		<li className="-lg:border-gray-800 -lg:border -lg:rounded-[10px] -lg:min-w-[200px] block min-h-full">
			<SVLink to={linkTo} className={cn('group relative h-full', isActive && 'disabled')}>
				<span
					className={cn(
						'absolute block h-full w-full rounded-[10px] bg-gray-900 opacity-0 group-hover:opacity-100',
						isActive && 'bg-gray-950 opacity-100',
					)}
				/>
				<span
					className={cn(
						'duration-slide relative flex h-full items-center gap-3 p-[6px] py-[4px]',
						isActive && openState === SidebarOpenState.MINIMAL
							? 'border-gray-400'
							: 'border-gray-900',
					)}
				>
					<span className="relative block h-10 w-10 flex-shrink-0 overflow-hidden rounded bg-gray-800">
						{thumbnails.length === 1 && (
							<div
								className="absolute inset-0 h-full w-full bg-cover bg-center"
								style={{ backgroundImage: `url(${thumbnails[0]})` }}
							/>
						)}
						{thumbnails.length === 2 && (
							<>
								<div
									className="absolute left-0 top-0 h-full w-1/2 bg-cover bg-center"
									style={{ backgroundImage: `url(${thumbnails[0]})` }}
								/>
								<div
									className="absolute right-0 top-0 h-full w-1/2 bg-cover bg-center"
									style={{ backgroundImage: `url(${thumbnails[1]})` }}
								/>
							</>
						)}
						{thumbnails.length === 3 && (
							<>
								<div
									className="absolute left-0 top-0 h-1/2 w-full bg-cover bg-center"
									style={{ backgroundImage: `url(${thumbnails[0]})` }}
								/>
								<div
									className="absolute bottom-0 left-0 h-1/2 w-1/2 bg-cover bg-center"
									style={{ backgroundImage: `url(${thumbnails[1]})` }}
								/>
								<div
									className="absolute bottom-0 right-0 h-1/2 w-1/2 bg-cover bg-center"
									style={{ backgroundImage: `url(${thumbnails[2]})` }}
								/>
							</>
						)}

						{thumbnails.length > 3 && (
							<>
								<div
									className="absolute left-0 top-0 h-1/2 w-1/2 bg-cover bg-center"
									style={{ backgroundImage: `url(${thumbnails[0]})` }}
								/>
								<div
									className="absolute right-0 top-0 h-1/2 w-1/2 bg-cover bg-center"
									style={{ backgroundImage: `url(${thumbnails[1]})` }}
								/>
								<div
									className="absolute bottom-0 left-0 h-1/2 w-1/2 bg-cover bg-center"
									style={{ backgroundImage: `url(${thumbnails[2]})` }}
								/>
								<div
									className="absolute bottom-0 right-0 h-1/2 w-1/2 bg-cover bg-center"
									style={{ backgroundImage: `url(${thumbnails[3]})` }}
								/>
							</>
						)}
					</span>
					<span className="flex min-w-0 flex-grow flex-col items-start">
						<span className="type-small truncate">All Saves</span>
						<span className="type-small truncate text-gray-500">All Saved Items</span>
					</span>
				</span>
			</SVLink>
		</li>
	);
};
const SortableItemWrapper = ({
	id,
	onRender,
}: {
	id: string;
	onRender: (isDragging: boolean, listeners: any) => React.ReactNode;
}) => {
	const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({
		id,
		animateLayoutChanges: () => true,
		transition: {
			duration: 150,
			easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
		},
	});

	const style = {
		transform: CSS.Transform.toString(transform),
		transition: isDragging ? 'transform 100ms cubic-bezier(0.25, 1, 0.5, 1)' : 'none',
		zIndex: isDragging ? DRAGGING_Z_INDEX : undefined,
		touchAction: 'none',
	};

	return (
		<>
			<div
				ref={setNodeRef}
				style={style}
				{...attributes}
				role="listitem"
				aria-roledescription="Sortable board item"
				aria-label="Sortable board item"
				className={cn(
					'duration-400 transform transition-transform ease-[cubic-bezier(0.25,1,0.5,1)]',
					SORTABLE_BOARD_ITEM_CLASS,
					isDragging && 'z-index-dragging',
				)}
			>
				{onRender(isDragging, listeners)}
			</div>
			<DragOverlay isDragging={isDragging} />
		</>
	);
};

const DragOverlay = ({ isDragging }: { isDragging: boolean }) => {
	if (!isDragging) return null;

	return (
		<div
			className="fixed inset-0 z-[9998] min-h-full w-full flex-1 cursor-grabbing"
			style={{ pointerEvents: 'auto' as const }}
		/>
	);
};

const _SidebarBoardsList = ({
	type,
	openState,
	boards,
	isOwner,
	onNewBoardClick,
	lastThumbnails,
	team,
	teamHomePath,
}: Omit<Props, 'onToggleSidebar' | 'children'> & { teamHomePath: string }) => {
	const [sortMethod] = usePersistentSetting(PersistentSettingKeys.BOARDS_SORT_METHOD);
	const { sensors, handleDragEnd } = useBoardReorder(boards);

	const reorderableBoards = useMemo(() => {
		return boards.filter((board) =>
			canReorderBoard(board.ownershipType, board.role ?? BoardUserRole.VIEWER, sortMethod),
		);
	}, [boards, sortMethod]);

	const nonReorderableBoards = useMemo(() => {
		return boards.filter(
			(board) =>
				!canReorderBoard(
					board.ownershipType,
					board.role ?? BoardUserRole.VIEWER,
					sortMethod,
				),
		);
	}, [boards, sortMethod]);

	return (
		<div
			className={cn(
				'duration-slide -lg:w-full -lg:whitespace-nowrap -lg:mb-7 -lg:mt-2 -sm:my-0 h-full overflow-visible transition-all ease-in-out',
				openState === SidebarOpenState.OPEN
					? 'w-[calc(240px+12px)]'
					: openState === SidebarOpenState.MINIMAL
					? 'w-[calc(40px+12px)]'
					: 'w-[calc(-12px+12px)]',
			)}
		>
			<ul
				className={cn(
					'duration-slide -lg:flex-row -lg:space-y-0 -lg:space-x-[6px] -lg:w-auto -lg:inline-flex flex flex-col space-y-[6px] transition-all ease-in-out',
					openState === SidebarOpenState.OPEN
						? 'w-[calc(240px+12px)]'
						: openState === SidebarOpenState.MINIMAL
						? 'w-[calc(40px+12px)]'
						: 'w-[calc(-12px+12px)]',
					type === TYPES.TEAM && 'mt-2',
				)}
			>
				{type === TYPES.TEAM && lastThumbnails?.[0] && (
					<_AllSavedItems
						thumbnails={lastThumbnails}
						openState={openState}
						teamHomePath={teamHomePath}
					/>
				)}

				{reorderableBoards.length > 0 ? (
					<DndContext
						sensors={sensors}
						collisionDetection={closestCenter}
						onDragEnd={handleDragEnd}
						measuring={{
							droppable: {
								strategy: MeasuringStrategy.Always,
							},
						}}
					>
						<SortableContext
							items={reorderableBoards.map((b) => b._id)}
							strategy={rectSortingStrategy}
						>
							{reorderableBoards.map((board) => (
								<SortableItemWrapper
									key={board._id}
									id={board._id}
									onRender={(isDragging, listeners) => (
										<_BoardItem
											board={board}
											openState={openState}
											type={type}
											team={team}
											teamHomePath={teamHomePath}
											isDragging={isDragging}
											boardRole={board.role ?? BoardUserRole.VIEWER}
											dragListeners={listeners}
										/>
									)}
								/>
							))}
						</SortableContext>
					</DndContext>
				) : null}

				{nonReorderableBoards.length > 0 && (
					<>
						{nonReorderableBoards.map((board) => (
							<_BoardItem
								key={board._id}
								board={board}
								openState={openState}
								type={type}
								team={team}
								teamHomePath={teamHomePath}
								boardRole={board.role ?? BoardUserRole.VIEWER}
							/>
						))}
					</>
				)}

				{isOwner ? (
					<NewBoardItem
						key="new-board"
						openState={openState}
						onClick={onNewBoardClick}
						type={type}
					/>
				) : null}
			</ul>
			<div className="-lg:h-2 h-16" />
		</div>
	);
};

const SVSidebarContent = React.memo(
	({
		type,
		openState,
		boards,
		isOwner,
		onNewBoardClick,
		onToggleSidebar,
		children,
		lastThumbnails,
		team,
	}: Props) => {
		const containerRef = useRef<HTMLDivElement>(null);
		const teamHomePath = useTeamHomePath();

		return (
			<div
				className={cn(
					'-lg:flex-col flex w-full overflow-visible',
					type === TYPES.TEAM ? '-sm:mt-20' : '',
				)}
				ref={containerRef}
			>
				<div className="relative flex flex-col">
					{onToggleSidebar && type !== TYPES.TEAM && (
						<div className="-lg:h-0 -lg:mb-0 mb-[7px] h-12 flex-shrink-0">
							<div
								className={cn(
									'duration-slide absolute transition-all ease-in-out',
									openState === SidebarOpenState.CLOSED
										? 'pl-[calc(var(--page-margin)-10px)]'
										: 'pl-[calc(var(--page-margin))]',
								)}
							>
								<_ToggleButton openState={openState} onToggle={onToggleSidebar} />
							</div>
						</div>
					)}
					<div
						className={cn(
							'duration-slide flex-shrink-0 flex-grow transition-all ease-in-out',
							openState === SidebarOpenState.OPEN
								? 'w-[calc(var(--page-margin)+240px+12px)]'
								: openState === SidebarOpenState.MINIMAL
								? 'w-[calc(var(--page-margin)+40px+12px)]'
								: 'w-0 overflow-hidden',
							type === TYPES.TEAM ? '-lg:flex -lg:w-full' : '-lg:hidden',
							openState === SidebarOpenState.CLOSED ? 'opacity-0' : 'opacity-100',
						)}
					>
						<div
							className={cn(
								'duration-slide -lg:flex -lg:w-full -lg:overflow-auto sticky left-0 top-0 z-10 h-full max-h-screen overflow-visible pl-[calc(var(--page-margin)-6px)] transition-all ease-in-out',
								openState === SidebarOpenState.OPEN
									? 'w-[calc(var(--page-margin)+240px+12px)]'
									: openState === SidebarOpenState.MINIMAL
									? 'w-[calc(var(--page-margin)+40px+12px)]'
									: 'w-0',
							)}
						>
							<div className="-lg:hidden">
								<SVTopNavSpacer containerRef={containerRef} />
							</div>
							<_SidebarBoardsList
								type={type}
								openState={openState}
								boards={boards}
								isOwner={isOwner}
								onNewBoardClick={onNewBoardClick}
								lastThumbnails={lastThumbnails}
								team={team ?? undefined}
								teamHomePath={teamHomePath}
							/>
						</div>
					</div>
				</div>
				<div className="z min-w-0 flex-1">{children}</div>
			</div>
		);
	},
);

export const SVSidebarContentTYPES = TYPES;

export default SVSidebarContent;
